import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaWindowClose } from "react-icons/fa";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import CommonModal from "./CommonModal";
import {
  catchError,
  byteCount,
  changeState,
  commonModalSetting,
  getKeyByValue,
  makeFormData,
  str2img,
} from "JS/common";
import {
  getNoticeDetail,
  noticeEdit,
  noticeWrite,
  uploadNoticeImg,
} from "JS/API";
import "react-quill/dist/quill.snow.css";

Quill.register("modules/imageResize", ImageResize);

const NoticeWrite = ({ id, setModal, setEditor }) => {
  const [info, setInfo] = useState({});
  const [byte, setByte] = useState({
    title: 0,
    context: 0,
  });
  const [services] = useState({});
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [mode, setMode] = useState("");
  const [, setUploadedImagePath] = useState("");
  const navigate = useNavigate();
  const quillRef = useRef(null);

  let prevent = false;

  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        try {
          const result = await uploadNoticeImg(file);

          console.log("서버 응답:", result);

          if (typeof result === "object" && result.data) {
            const responseData = Array.isArray(result.data)
              ? result.data
              : [result.data];

            if (responseData.length > 0 && responseData[0].path) {
              const imagePath = responseData[0].path;
              console.log("추출된 이미지 경로:", imagePath);

              setUploadedImagePath(imagePath);

              const quill = quillRef.current.getEditor();
              const range = quill.getSelection(true);

              // 이미지 삽입
              quill.insertEmbed(range.index, "image", imagePath);

              // 줄바꿈 삽입 (다음 줄 확보)
              quill.insertText(range.index + 1, "\n");

              // 커서 이동
              quill.setSelection(range.index + 2);
            } else {
              console.error("이미지 경로를 찾을 수 없습니다:", responseData);
              throw new Error("이미지 URL을 찾을 수 없습니다");
            }
          } else {
            console.error("잘못된 응답 형식:", result);
            throw new Error("서버 응답이 올바르지 않습니다");
          }
        } catch (error) {
          console.error("이미지 업로드 중 오류 발생:", error);
          commonModalSetting(
            setAlertBox,
            true,
            "alert",
            "이미지 업로드 중 오류가 발생했습니다."
          );
        }
      }
    };
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      ["image"],
      [{ align: [] }, { color: [] }, { background: [] }],
      ["clean"],
    ],
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "link",
    "image",
    "align",
    "color",
    "background",
  ];

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");
      toolbar.addHandler("image", imageHandler);
    }
  }, [quillRef.current]);

  //= 서비스 옵션 렌더
  const renderServiceList = () => {
    return Object.values(services).reduce((acc, service) => {
      return (
        <>
          {acc}
          <option value={getKeyByValue(services, service)}>{service}</option>
        </>
      );
    }, <></>);
  };

  //= 수정일 때 기존 내용 불러오기
  const getDetail = async () => {
    if (prevent) return;
    prevent = true;
    setTimeout(() => {
      prevent = false;
    }, 200);
    const result = await getNoticeDetail(id);
    if (typeof result === "object") {
      const { title, context, img_url } = result?.data;
      if (img_url) {
        setUploadedImagePath(img_url);
      }
      const content = str2img(img_url, context);
      setInfo({
        title: title,
        context: content,
      });
    } else return catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 수정
  const editNotice = async () => {
    const editor = quillRef.current.getEditor().root;
    const editorHtml = editor.innerHTML;

    const formData = makeFormData();
    formData.append("service_code", info.service_code);
    formData.append("title", info.title);
    formData.append("context", editorHtml);

    const result = await noticeEdit(id, formData);
    if (typeof result === "object") {
      setAlert("completeEdit");
      commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "성공적으로 수정 되었습니다."
      );
    } else return catchError(result, navigate, setAlertBox, setAlert);
  };

  //= 등록
  const writeNotice = async () => {
    if (!info.title && !info.context) {
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "제목과 내용을 입력해 주세요."
      );
    } else if (!info.title) {
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "제목을 입력해 주세요."
      );
    } else if (!info.context) {
      return commonModalSetting(
        setAlertBox,
        true,
        "alert",
        "내용을 입력해 주세요."
      );
    } else {
      const editor = quillRef.current.getEditor().root;
      const editorHtml = editor.innerHTML;

      const formData = makeFormData();
      formData.append("service_code", info.service_code);
      formData.append("title", info.title);
      formData.append("context", editorHtml);

      const result = await noticeWrite(formData);
      if (typeof result === "object") {
        setAlert("completeApply");
        commonModalSetting(
          setAlertBox,
          true,
          "alert",
          "성공적으로 등록 되었습니다."
        );
      } else return catchError(result, navigate, setAlertBox, setAlert);
    }
  };

  useEffect(() => {
    if (!!id) {
      setMode("edit");
      getDetail();
    } else {
      setMode("write");
      changeState(setInfo, "service_code", 100);
    }
  }, []);

  useEffect(() => {
    byteCount(info.context, setInfo, setByte, "context", 3000);
  }, [info.context]);

  useEffect(() => {
    byteCount(info.title, setInfo, setByte, "title", 300);
  }, [info.title]);

  return (
    <>
      <div className="modal-background">
        <div className="modal editor">
          <div className="topBar">
            <div>
              {mode === "write" && (
                <select
                  value={info.service_code}
                  onChange={(e) =>
                    changeState(setInfo, "service_code", Number(e.target.value))
                  }
                >
                  {renderServiceList()}
                </select>
              )}
              <input
                type="text"
                value={info.title}
                onChange={(e) => changeState(setInfo, "title", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                  }
                }}
              />
              <div className="viewBytes">
                <span>{byte.title}</span>/{300}
              </div>
            </div>
            <div
              onClick={() => {
                setAlert("confirmCancel");
                commonModalSetting(
                  setAlertBox,
                  true,
                  "confirm",
                  `정말 취소하시겠습니까?<br />지금까지 ${
                    mode === "edit" ? "수정" : "작성"
                  }된 내용은 반영되지 않습니다.`
                );
              }}
            >
              <FaWindowClose />
            </div>
          </div>
          <hr />
          <div
            className="quill-wrap"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
              }
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              modules={modules}
              formats={formats}
              value={info?.context}
              onChange={(c, d, s, editor) =>
                changeState(setInfo, "context", editor.getContents())
              }
            />
          </div>
          <div className="footer">
            <div className="viewBytes">
              <span>{byte.context}</span>/3000
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  setAlert("confirmCancel");
                  commonModalSetting(
                    setAlertBox,
                    true,
                    "confirm",
                    `정말 취소하시겠습니까?<br />지금까지 ${
                      mode === "edit" ? "수정" : "작성"
                    }된 내용은 반영되지 않습니다.`
                  );
                }}
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  if (id) {
                    editNotice();
                  } else {
                    writeNotice();
                  }
                }}
              >
                완료
              </button>
            </div>
          </div>
        </div>
      </div>
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (
              alert === "completeEdit" ||
              (alert === "confirmCancel" && mode === "edit")
            ) {
              setEditor(false);
              setModal(true);
            } else if (
              alert === "completeApply" ||
              (alert === "confirmCancel" && mode === "write")
            )
              setEditor(false);
            else if (alert === "logout") navigate("/");
            else return;
          }}
        />
      )}
    </>
  );
};

export default NoticeWrite;